import { useState, useEffect, useMemo } from "react";
import { Box, Tabs, Tab, Typography, useMediaQuery } from "@mui/material";
import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import AltRouteOutlinedIcon from "@mui/icons-material/AltRouteOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import MultipleStopOutlinedIcon from "@mui/icons-material/MultipleStopOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import PauseCircleOutlineOutlinedIcon from "@mui/icons-material/PauseCircleOutlineOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import TimelineOutlinedIcon from "@mui/icons-material/TimelineOutlined";
import { useTranslation } from "react-i18next";
import { useUser } from "../../pages/login/UserContextPersistent";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import {
  buildReportsTabsFromLayout,
  getLegacyReportsTabs,
} from "../../layout/reportsLayoutUtils";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import {
  ANALYTICS_MAIN_TAB_KEYS,
  EXPORT_REPORTS_TAB_KEYS,
  OVERVIEW_TAB_KEYS,
  REPORTS_ANALYTICS_TAB_INDEX_BY_KEY,
  REPORTS_TAB_VALUE_BY_KEY,
} from "../../config/reportsModuleCatalog";
import ReportGenerator from "./ReportGenerator";
import AuditLogReport from "./AuditLogReport";
import { reportsApi } from "./reportsApi";
import "./reports.css";
import { useTheme } from "@mui/material/styles";
import AnalyticsChart from "./Analytics/AnalyticsChart";
import Overview from "../reports/Analytics/Overview";
import SpeedAnalytics from "../reports/Analytics/SpeedAnalytics";
import TripAnalytics from "../reports/Analytics/TripAnalytics";
import RouteAnalytics from "../reports/Analytics/RouteAnalytics";
import VehicleAnalytics from "../reports/Analytics/VehicleAnalytics";
import DriverBehaviorAnalytics from "../reports/Analytics/DriverBehaviorAnalytics";
// import BottomSection from "../reports/BottomSection";
import ReplayVehicle from "../reports/ReplayVehicle";
import ManageDataMain from "./ManageDataMain";

const Reports = ({ school = false, dateRange } = {}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useUser();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const reportsPageId = school ? PAGE_IDS.SCHOOL_REPORTS : PAGE_IDS.FLEET_REPORTS;
  const { layout: moduleLayout } = usePageLayout(useJsonLayout ? reportsPageId : null);

  const legacyMainTabs = useMemo(
    () =>
      getLegacyReportsTabs({
        t,
        features: state?.features,
      }),
    [t, state?.features],
  );

  const layoutMainTabs = useMemo(() => {
    if (!moduleLayout) return null;
    return buildReportsTabsFromLayout(moduleLayout, {
      t,
      features: state?.features,
      school,
    });
  }, [moduleLayout, t, state?.features, school]);

  const mainTabs = layoutMainTabs || legacyMainTabs;

  const [mainTab, setMainTab] = useState(() => {
    const tabKey = searchParams.get("tab");
    return REPORTS_TAB_VALUE_BY_KEY[tabKey] ?? 0;
  });
  const [reportTab, setReportTab] = useState(0);
  const [vehicles, setVehicles] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [stops, setStops] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState([]);
  const [devices, setDevices] = useState([]);

  const roleLabel = String(
    state?.user?.role?.name || state?.user?.role || ""
  ).toLowerCase();
  const canViewAuditLog =
    roleLabel.includes("admin") || state?.user?.email === "sales@routeeye.io";

  const reportTabs = [
    { label: "Activity Report", reportType: "activity", icon: TimelineOutlinedIcon },
    { label: "Overspeed Report", reportType: "overspeed", icon: SpeedOutlinedIcon },
    { label: "Inventory Report", reportType: "inventory", icon: Inventory2OutlinedIcon },
    { label: "Daily Runtime Report", reportType: "runtime", icon: AccessTimeOutlinedIcon },
    { label: "Trip Report", reportType: "trip", icon: RouteOutlinedIcon },
    { label: "Route Deviation Report", reportType: "deviation", icon: AltRouteOutlinedIcon },
    { label: "SOS Alert Report", reportType: "sos", icon: NotificationsActiveOutlinedIcon },
    { label: "Halt Report", reportType: "halt", icon: PauseCircleOutlineOutlinedIcon },
    { label: "Stop Entry/Exit Report", reportType: "entryExit", icon: MultipleStopOutlinedIcon },
    ...(canViewAuditLog
      ? [{ label: "Audit Log", reportType: "audit", icon: HistoryOutlinedIcon }]
      : []),
  ];

  useEffect(() => {
    const tabKey = searchParams.get("tab");
    const nextValue = REPORTS_TAB_VALUE_BY_KEY[tabKey] ?? mainTabs[0]?.value ?? 0;
    if (mainTabs.length > 0 && mainTabs.some((tab) => tab.value === nextValue) && nextValue !== mainTab) {
      setMainTab(nextValue);
    }
  }, [searchParams, mainTabs, mainTab]);

  const openReportsTab = (tab) => {
    if (!tab) return;
    setMainTab(tab.value);
    const next = new URLSearchParams(searchParams);
    if (!tab.tabKey || tab.tabKey === "overview" || tab.tabKey === "analyticsOverview") {
      next.delete("tab");
    } else {
      next.set("tab", tab.tabKey);
    }
    setSearchParams(next, { replace: true });
  };

  useEffect(() => {
    const fetchAllData = async () => {
      setLoading(true);
      try {
        const [
          routesData,
          vehiclesData,
          driversData,
          sitesData,
          stopsData,
          usersData,
          devicesData,
        ] = await Promise.all([
          reportsApi.getRoutesByOrganization(),
          reportsApi.getVehiclesByOrganization(),
          reportsApi.getDriversByOrganization(),
          reportsApi.getSitesByOrganization(),
          reportsApi.getStopsByOrganization(),
          reportsApi.getAllUsersByOrganization(),
          reportsApi.getTrackingDevicesByOrganization(),
        ]);
        setRoutes(routesData);
        setVehicles(vehiclesData);
        setDrivers(driversData);
        setSites(sitesData);
        setStops(stopsData);
        setUsers(usersData.users || []);
        setDevices(devicesData || []);
      } catch (err) {
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  const handleReportTabChange = (event, newValue) => {
    setReportTab(newValue);
  };

  const TabPanel = ({ children, value, index }) => (
    <Box role="tabpanel" hidden={value !== index} sx={{ width: "100%" }}>
      {value === index && children}
    </Box>
  );

  // Get the actual index for the current mainTab value
  const getCurrentTabIndex = () => {
    return mainTabs.findIndex((tab) => tab.value === mainTab);
  };

  const getTabPanelIndex = (tabKey) => {
    return mainTabs.findIndex((tab) => tab.tabKey === tabKey);
  };

  const hasAnyTab = (tabKeys) =>
    mainTabs.some((tab) => tabKeys.includes(tab.tabKey));

  const currentTabKey = mainTabs.find((tab) => tab.value === mainTab)?.tabKey;
  const isAnalyticsMainTab = ANALYTICS_MAIN_TAB_KEYS.includes(currentTabKey);
  const isOverviewMainTab = OVERVIEW_TAB_KEYS.includes(currentTabKey);
  const analyticsTabIndex = REPORTS_ANALYTICS_TAB_INDEX_BY_KEY[currentTabKey] ?? 0;

  const handleViewMoreAnalytics = () => {
    const speedTab = mainTabs.find((tab) => tab.tabKey === "speedAnalytics");
    if (speedTab) {
      openReportsTab(speedTab);
      return;
    }
    navigate("/reports/analytics");
  };

  const renderTabLabel = (tab) => {
    if (tab.icon !== "download" && tab.tabKey !== "exportReports") {
      return tab.label;
    }

    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />
        <span>{tab.label}</span>
      </Box>
    );
  };

  return (
    <Box sx={{ width: "100%" }}>
      {/* OLD TAB BAR
      <Box
        sx={{
          borderBottom: 1,
          borderColor: "divider",
          m: 4,
        }}
      >
        <Tabs
          value={getCurrentTabIndex()}
          onChange={(event, newIndex) => {
            const selectedTab = mainTabs[newIndex];
            if (selectedTab) {
              setMainTab(selectedTab.value);
            }
          }}
          variant="fullWidth"
          sx={{
            color: "#414958",
            borderBottom: 1,
            borderColor: "divider",
            "& .MuiTabs-flexContainer": {
              gap: "0px",
            },
            "& .MuiTab-root": {
              textTransform: "none",
              fontSize: "20px",
              fontFamily: "Roboto",
              fontWeight: 500,
              lineHeight: "100%",
              letterSpacing: "0%",
              minHeight: "56px",
              border: "1px solid #95A0B3",
              color: "#666666",
              backgroundColor: "#FFFFFF",
              "&.Mui-selected": {
                backgroundColor: "#20A8E0",
                color: "#FFFFFF",
              },
              "&:first-of-type": {
                borderTopLeftRadius: "8px",
                borderBottomLeftRadius: "8px",
              },
              "&:last-of-type": {
                borderTopRightRadius: "8px",
                borderBottomRightRadius: "8px",
              },
              borderRight: "none",
              "&:last-child": {
                borderRight: "1px solid #95A0B3",
              },
            },
            "& .MuiTabs-indicator": {
              display: "none",
            },
          }}
        >
          {mainTabs.map((tab) => (
            <Tab key={tab.tabKey || tab.value} label={tab.label} />
          ))}
        </Tabs>
      </Box>
      */}

      <Box className="reports-new-tabs">
        <Tabs
          value={getCurrentTabIndex() === -1 ? false : getCurrentTabIndex()}
          onChange={(event, newIndex) => {
            openReportsTab(mainTabs[newIndex]);
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {mainTabs.map((tab) => (
            <Tab
              key={tab.tabKey || tab.value}
              disableRipple
              label={renderTabLabel(tab)}
            />
          ))}
        </Tabs>
      </Box>

      {hasAnyTab(ANALYTICS_MAIN_TAB_KEYS) && isAnalyticsMainTab && (
        <Box sx={{ px: { xs: 2, md: 3 }, pb: 2, background: "#f4f4f5" }}>
          {isOverviewMainTab ? (
            <Overview
              vehicles={vehicles}
              selectedDateRange={dateRange?.selectedDateRange || "today"}
              customDateRange={dateRange?.customDateRange || null}
              onViewAll={handleViewMoreAnalytics}
            />
          ) : currentTabKey === "speedAnalytics" ? (
            <SpeedAnalytics
              vehiclesData={vehicles}
              selectedDateRange={dateRange?.selectedDateRange || "today"}
              customDateRange={dateRange?.customDateRange || null}
            />
          ) : currentTabKey === "tripAnalytics" ? (
            <TripAnalytics
              selectedDateRange={dateRange?.selectedDateRange || "today"}
              customDateRange={dateRange?.customDateRange || null}
            />
          ) : currentTabKey === "routeAnalytics" ? (
            <RouteAnalytics
              routesData={routes}
              selectedDateRange={dateRange?.selectedDateRange || "today"}
              customDateRange={dateRange?.customDateRange || null}
            />
          ) : currentTabKey === "vehicleAnalytics" ? (
            <VehicleAnalytics
              vehiclesData={vehicles}
              devicesData={devices}
              selectedDateRange={dateRange?.selectedDateRange || "today"}
              customDateRange={dateRange?.customDateRange || null}
            />
          ) : currentTabKey === "driverBehavior" ? (
            <DriverBehaviorAnalytics
              school={school}
              selectedDateRange={dateRange?.selectedDateRange || "today"}
              customDateRange={dateRange?.customDateRange || null}
            />
          ) : (
            /* Previous Route and Vehicle Analytics were rendered here through
               AnalyticsChart. Keep that implementation unchanged for legacy use. */
            <AnalyticsChart
              vehiclesData={vehicles}
              routesData={routes}
              driversData={drivers}
              stopsData={stops}
              sitesData={sites}
              usersData={users}
              overview={false}
              school={school}
              hideHeader
              hideInternalTabs
              activeTab={analyticsTabIndex}
              selectedDateRange={dateRange?.selectedDateRange || "today"}
              customDateRange={dateRange?.customDateRange || null}
              onViewMore={handleViewMoreAnalytics}
            />
          )}
        </Box>
      )}

      {false && mainTabs.some((tab) => tab.tabKey === "analyticsOverview") && (
        <TabPanel
          value={getCurrentTabIndex()}
          index={getTabPanelIndex("analyticsOverview")}
        >
          <Box sx={{ p: 2, background: "#FFFFFF", borderRadius: "10px" }}>
            <AnalyticsChart
              vehiclesData={vehicles}
              routesData={routes}
              driversData={drivers}
              stopsData={stops}
              sitesData={sites}
              usersData={users}
              overview={true}
              school={school}
            />
          </Box>
        </TabPanel>
      )}

      {hasAnyTab(EXPORT_REPORTS_TAB_KEYS) &&
        EXPORT_REPORTS_TAB_KEYS.includes(currentTabKey) && (
          <Box
            sx={{
              background: "#FFFFFF",
              borderRadius: "10px",
              p: 2,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.5,
                backgroundColor: "#EEF6FF",
                border: "1px solid #D6E8FF",
                borderRadius: "8px",
                px: 2,
                py: 1.5,
                mb: 3,
              }}
            >
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  borderRadius: "50%",
                  backgroundColor: "#2563EB",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 14 }} />
              </Box>
              <Box>
                <Typography
                  sx={{
                    color: "#1F2937",
                    fontSize: "14px",
                    fontWeight: 700,
                    lineHeight: 1.2,
                  }}
                >
                  {t("About Report Export")}
                </Typography>
                <Typography
                  sx={{
                    color: "#6B7280",
                    fontSize: "12px",
                    lineHeight: 1.35,
                  }}
                >
                  {t(
                    "All reports are generated based on the selected filters. You can download the report in Excel (XLSX), PDF and Word format.",
                  )}
                </Typography>
              </Box>
            </Box>

            <Box
              sx={{
                display: "flex",
                flexDirection: isSmallScreen ? "column" : "row",
              background: "#FFFFFF",
                borderRadius: "10px",
                gap: { xs: 2, md: 4 },
            }}
          >
            <Tabs
              orientation={isSmallScreen ? "horizontal" : "vertical"}
              value={reportTab}
              onChange={handleReportTabChange}
              variant={isSmallScreen ? "scrollable" : "standard"}
              scrollButtons={isSmallScreen ? "auto" : false}
              allowScrollButtonsMobile={true}
              sx={{
                marginBottom: isSmallScreen ? "20px" : "0px",
                minWidth: isSmallScreen ? "100%" : "250px",
                width: isSmallScreen ? "100%" : "250px",
                flexShrink: 0,
                ...(isSmallScreen && {
                  "& .MuiTabs-scroller": {
                    overflow: "auto !important",
                  },
                }),
                "& .MuiTab-root": {
                  textTransform: "none",
                  alignItems: "stretch",
                  justifyContent: "flex-start",
                  textAlign: "left",
                  padding: 0,
                  minHeight: "56px",
                  color: "#1F2937",
                  borderBottom: "1px solid #E5E7EB",
                  backgroundColor: "#FFFFFF",

                  ...(isSmallScreen && {
                    minWidth: "210px",
                    flexShrink: 0,
                  }),
                  "&.Mui-selected": {
                    backgroundColor: "#183059",
                    color: "#fff",
                  },
                },
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTabs-scrollButtons": {
                  "&.Mui-disabled": {
                    opacity: 0.3,
                  },
                },
              }}
            >
              {reportTabs.map((tab, index) => {
                const ReportIcon = tab.icon;

                return (
                  <Tab
                    key={index}
                    label={
                      <Box
                        sx={{
                          width: "100%",
                          display: "flex",
                          alignItems: "center",
                          gap: 1.5,
                          px: 2,
                          py: 1,
                        }}
                      >
                        <Box
                          sx={{
                            width: 34,
                            height: 34,
                            borderRadius: "50%",
                            backgroundColor:
                              reportTab === index ? "#FFE600" : "#64748B",
                            color: reportTab === index ? "#183059" : "#FFFFFF",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            flexShrink: 0,
                          }}
                        >
                          <ReportIcon sx={{ fontSize: 19 }} />
                        </Box>
                        <Typography
                          sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            color: reportTab === index ? "#FFFFFF" : "#1F2937",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {t(tab.label)}
                        </Typography>
                      </Box>
                    }
                  />
                );
              })}
            </Tabs>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {reportTabs.map((tab, index) => (
                <TabPanel key={index} value={reportTab} index={index}>
                  {tab.reportType === "audit" ? (
                    <AuditLogReport users={users} t={t} />
                  ) : (
                    <ReportGenerator
                      reportType={tab.reportType}
                      vehicles={vehicles}
                      routes={routes}
                      drivers={drivers}
                      stops={stops}
                      sites={sites}
                      users={users}
                      devices={devices}
                      setSites={setSites}
                      t={t}
                    />
                  )}
                </TabPanel>
              ))}
            </Box>
            </Box>
          </Box>
      )}

      {mainTabs.some((tab) => tab.tabKey === "tripReplays") && (
        <TabPanel
          value={getCurrentTabIndex()}
          index={getTabPanelIndex("tripReplays")}
        >
          <Box sx={{ p: 2, background: "#FFFFFF", borderRadius: "10px" }}>
            <ReplayVehicle t={t} />
          </Box>
        </TabPanel>
      )}

      {mainTabs.some((tab) => tab.tabKey === "dataManagement") && (
        <TabPanel
          value={getCurrentTabIndex()}
          index={getTabPanelIndex("dataManagement")}
        >
          <Box sx={{ p: 2, background: "#FFFFFF", borderRadius: "10px" }}>
            <ManageDataMain school={school} />
          </Box>
        </TabPanel>
      )}
    </Box>
  );
};

export default Reports;
